import { InvoiceSummaryResp } from '@monetize/types/app';
import {
  FilterOptionType,
  FilterType,
  FilterTypeEqual,
  FilterTypeOperator,
  GetListApiFilter,
  RenderTableFilterOptionProps,
} from '@monetize/types/common';
import React, { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { useGetListData } from '../../../../api/queryUtils';
import {
  MFlex,
  MPageContentContainer,
  MPageHeader,
  MPageSearchInput,
  MSwitch,
  MText,
} from '../../../../components/Monetize';
import { MDataTableFilter } from '../../../../components/Monetize/DataTable';
import { CurrencyTableFilterOptionContent } from '../../../../components/Monetize/DataTable/FilterOptions/CurrencyTableFilterOptionContent';
import {
  buildHideTestAccountFilterOption,
  TEST_ACCOUNT_FILTER_KEY,
} from '../../../../components/Monetize/DataTable/FilterOptions/HideTestAccountFilterOption';
import {
  ExternalFilterDef,
  useExternalFilters,
} from '../../../../components/Monetize/DataTableNew/useExternalFilters';
import { ExportEntityButton } from '../../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../../components/Monetize/RefreshTableButton';
import {
  tableFilterSelector,
  tablePagerSelector,
} from '../../../../store/global.store';
import {
  FilterStateKeys,
  getDefaultPager,
} from '../../../../store/store.types';
import {
  buildFilterParamsRequestObject,
  getIsTrulyEmptyList,
  transformTableFilterValue,
} from '../../../../utils';
import { InvoiceList } from '../../../Invoices/InvoiceList';

const renderSentInvoiceToggleOptionContent = ({
  filter,
  filterOption,
  handleFilterChange,
}: RenderTableFilterOptionProps) => {
  const internalFilter = filter as FilterTypeEqual;
  const { options } = filterOption;

  const falseValue = options?.falseValue ?? '';
  const trueValue = options?.trueValue ?? '';
  const isChecked = internalFilter?.value === trueValue;

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked ? trueValue : falseValue;
    handleFilterChange(newValue, filterOption);
  };

  return (
    <MFlex alignItems="center" my="2" justifyContent="space-between">
      <MText mr="4">Hide Sent Invoices</MText>
      <MSwitch size="md" isChecked={isChecked} onChange={handleToggleChange} />
    </MFlex>
  );
};

const INVOICE_LIST_FILTER_DEFS: ExternalFilterDef[] = [
  { key: TEST_ACCOUNT_FILTER_KEY, columnHeader: 'Hide Test Account' },
];

export const BillingDashboard = () => {
  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(FilterStateKeys.INVOICE_LIST),
  );

  const [filters, setFilters] = useState<FilterType[]>(initialFilters);
  const [pager, setPager] = useAtom(
    tablePagerSelector(FilterStateKeys.INVOICE_LIST),
  );
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { extraActiveFilters } = useExternalFilters({
    filters,
    setFilters,
    pager,
    setPager,
    filterDefs: INVOICE_LIST_FILTER_DEFS,
  });

  const [filterParams, setFilterParams] = useState<GetListApiFilter>(() =>
    buildFilterParamsRequestObject(
      transformTableFilterValue(filters),
      searchTerm,
      'invoiceNumber',
    ),
  );

  useEffect(() => {
    setFilterParams(
      buildFilterParamsRequestObject(
        transformTableFilterValue(filters),
        searchTerm,
        'invoiceNumber',
      ),
    );
  }, [filters, searchTerm, pager]);

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  const {
    isLoading,
    data: invoices,
    isError,
    error,
    refetch,
  } = useGetListData<InvoiceSummaryResp>(
    'invoices',
    { config: pager, filters: filterParams },
    {
      meta: { showErrorToast: true },
    },
  );

  const onResetFilter = () => {
    setFilters([]);
    setPager(getDefaultPager(FilterStateKeys.INVOICE_LIST));
    setSearchTerm('');
  };

  const filterOptions: FilterOptionType[] = [
    {
      title: '',
      key: 'sent',
      operator: FilterTypeOperator.TOGGLE,
      renderOptionContent: renderSentInvoiceToggleOptionContent,
      options: {
        falseValue: '',
        trueValue: 'false',
      },
    },
    buildHideTestAccountFilterOption('Hide Test Account Invoices'),
    {
      title: 'Currency',
      key: 'currency',
      operator: FilterTypeOperator.EQUAL,
      renderOptionContent: (props) => (
        <CurrencyTableFilterOptionContent {...props} />
      ),
    },
  ];

  const filterComponentReset = React.useRef<any>(null);
  const searchComponentReset = React.useRef<any>(null);
  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading: isLoading,
    totalElements: invoices?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  return (
    <MPageContentContainer>
      <MPageHeader title="Invoices" hideContent={isTrulyEmptyList}>
        <MFlex>
          {!isTrulyEmptyList && (
            <>
              <MPageSearchInput
                placeholderKey="Invoice Number"
                value={searchTerm}
                count={invoices?.totalElements}
                mode="alwaysExpanded"
                onChange={setSearchTerm}
              />
              <RefreshTableButton refetch={refetch} />
              <MDataTableFilter
                filters={filters}
                filterOptions={filterOptions}
                setFilters={setFilters}
                onResetFilter={onResetFilter}
                resetFilter={filterComponentReset}
              />
              <ExportEntityButton
                entity="invoices"
                filters={filters}
                searchKey="invoiceNumber"
                searchTerm={searchTerm}
                sortField={pager.sortField}
                sortOrder={pager.sortOrder}
              />
            </>
          )}
        </MFlex>
      </MPageHeader>
      <InvoiceList
        omitBillGroup
        filters={filters}
        setFilters={setFilters}
        onResetFilter={onResetFilter}
        setPager={setPager}
        pager={pager}
        isLoading={isLoading}
        invoices={invoices}
        filterComponentReset={filterComponentReset}
        showAccount={true}
        searchTerm={searchTerm}
        searchComponentReset={searchComponentReset}
        source="dashboard"
        isError={isError}
        error={error}
        refetch={refetch}
        extraActiveFilters={extraActiveFilters}
      />
    </MPageContentContainer>
  );
};
